import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallRoute } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Modal, ModalControlModalProps, useAlert, Text } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { usePhoneSettingsShallowStore } from '../../../store/settings';
import { trackingId } from '../../../tracking';
import { PhoneNumber } from '../../../views/settings/types';
import { logError } from '../helpers';
import { ReassignPhoneNumber, SuccessMessageDisplay, UNASSIGNED_CALL_ROUTE_ID } from './reassign-phone-number-modal';

export const DeleteCallRouteModal = ({
  deleteModalProps,
  callRouteId,
  callRouteName,
  phoneNumbers,
  callRoutes,
  onSuccess,
}: {
  deleteModalProps: ModalControlModalProps;
  callRouteId?: string;
  callRouteName?: string;
  phoneNumbers: PhoneNumber[];
  callRoutes?: CallRoute[];
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { mutateAsync: deleteCallRoute } = CallRouteV1.Mutations.useDeleteMutation({});
  const queryClient = useQueryClient();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const [remainingPhoneNumbers, setRemainingPhoneNumbers] = useState<PhoneNumber[]>(phoneNumbers);

  const { mutate: updateCallRoutePhoneNumbers, isLoading: updateCallRoutePhoneNumbersIsLoading } =
    CallRouteV1.Mutations.useUpdatePhoneNumbersMutation();

  const currentPhoneNumber = remainingPhoneNumbers[0];

  const callRouteOptions = useMemo(() => {
    // Filter out the call route being deleted from the list of call routes
    // so that it doesn't show up in the dropdown.
    return (
      callRoutes?.filter((cr) => cr.callRouteId !== callRouteId).sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [callRoutes, callRouteId]);

  const handleReassignPhoneNumber = (newCallRouteId: string, reassignAll: boolean) => {
    let phoneNumberIds: string[];

    let reassignedPhoneNumbers: PhoneNumber[] = [];

    if (newCallRouteId === UNASSIGNED_CALL_ROUTE_ID) {
      if (!callRouteId) {
        logError('No call route ID provided for unassignment', 'DeleteCallRouteModal:handleReassignPhoneNumber', {
          callRouteId,
          reassignAll,
        });
        alerts.error(t('No Call Route ID provided for unassignment.'));
        return;
      }

      // If unassigning, we need to remove the phone numbers from the call route
      if (reassignAll) {
        phoneNumberIds = []; // If reassigning all, we will remove all phone numbers from the call route
        reassignedPhoneNumbers = remainingPhoneNumbers; // If reassigning all, we will remove all phone numbers
      } else {
        // If unassigning only the current phone number, we will remove it from the call route
        phoneNumberIds = remainingPhoneNumbers.filter((num) => num.id !== currentPhoneNumber.id).map((num) => num.id);
        reassignedPhoneNumbers = [currentPhoneNumber]; // If reassigning only the current phone number
      }
      newCallRouteId = callRouteId;
    } else {
      // If reassigning to a new call route, we need to get the IDs of the phone numbers to reassign
      const newPhoneNumberIds = reassignAll ? remainingPhoneNumbers.map((num) => num.id) : [currentPhoneNumber.id];
      reassignedPhoneNumbers = reassignAll ? remainingPhoneNumbers : [currentPhoneNumber];
      const callRouteToReassign = callRoutes?.find((route) => route.callRouteId === newCallRouteId);

      if (!callRouteToReassign) {
        logError('No call route found for reassignment', 'DeleteCallRouteModal:handleReassignPhoneNumber', {
          newCallRouteId,
          reassignAll,
          callRouteIds: callRoutes?.map((route) => route.callRouteId),
        });
        alerts.error(t('No Call Route found for reassignment.'));
        return;
      }

      const callRoutePhoneNumberIds = callRouteToReassign.phoneNumberIds;
      phoneNumberIds = newPhoneNumberIds.concat(callRoutePhoneNumberIds); // Combine with existing phone numbers in the new call route
    }

    // Call API to reassign the phone number
    updateCallRoutePhoneNumbers(
      { phoneNumberIds, callRouteId: newCallRouteId },
      {
        onSuccess: () => {
          alerts.success({
            message: <SuccessMessageDisplay phoneNumbers={reassignedPhoneNumbers} />,
          });
          // Remove the phone number from the unassigned list
          if (reassignAll) {
            // If reassigning all, remove all remaining phone numbers
            setRemainingPhoneNumbers([]);
          } else {
            // If reassigning only the current phone number, remove just that one
            setRemainingPhoneNumbers((prev) => prev.filter((num) => num.id !== currentPhoneNumber.id));
          }
        },
        onError: () => {
          alerts.error(t('Failed to reassign Phone Number.'));
        },
      }
    );
  };

  return (
    <>
      {/* If there are no phone numbers assigned to the call route, just display the confirmation modal */}
      {!remainingPhoneNumbers.length && (
        <ConfirmationModal
          {...deleteModalProps}
          maxWidth={600}
          title={t('Delete Call Route?')}
          message={
            <Trans t={t}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: ${theme.spacing(2)};
                `}
              >
                <Text>Deleting this route is permanent and may disrupt active workflows.</Text>

                {callRouteName && (
                  <Text>
                    Are you sure you want to delete<strong> {callRouteName}</strong>?
                  </Text>
                )}
              </div>
            </Trans>
          }
          cancelLabel={t('No, Cancel')}
          confirmLabel={t('Yes, Delete')}
          destructive
          cancelTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::cancel-btn',
          })}
          confirmTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'delete::confirm-btn',
          })}
          onCancel={async () => {
            deleteModalProps.onClose();
          }}
          onConfirm={async () => {
            if (callRouteId) {
              deleteCallRoute({ callRouteId })
                .then(() => {
                  queryClient.invalidateQueries([phoneTenantId, ...queryKeys.settings.listOverrides()]);
                  alerts.success(t('Call Route deleted successfully.'));
                  deleteModalProps.onClose();
                  onSuccess?.();
                })
                .catch((error) => {
                  logError('Error deleting Call Route', 'DeleteCallRouteModal:onConfirm', {
                    callRouteId,
                    error,
                  });
                  alerts.error(t('Failed to delete Call Route.'));
                });
            }
          }}
        />
      )}

      {remainingPhoneNumbers.length > 0 && (
        <Modal {...deleteModalProps} minWidth={600}>
          <ReassignPhoneNumber
            remainingCount={phoneNumbers.length}
            currentPhoneNumber={currentPhoneNumber}
            callRouteName={callRouteName}
            remainingPhoneNumbers={remainingPhoneNumbers}
            callRoutes={callRouteOptions}
            isLoading={updateCallRoutePhoneNumbersIsLoading}
            onClose={deleteModalProps.onClose}
            handleReassignPhoneNumber={handleReassignPhoneNumber}
          />
        </Modal>
      )}
    </>
  );
};
